/**
 * Tracking Helpers — visitor_sessions + phone_clicks funnel data
 * Praat via de backend-API (/api/data/*) met een Bearer-JWT.
 * De aggregatie gebeurt server-side; deze functies zijn dunne wrappers
 * met behoud van de oude signatures en return-shapes.
 */

import { apiGet } from './apiClient';

/**
 * Date vanaf wanneer server-side tracking betrouwbaar is.
 * (Behouden voor componenten/consumers die ernaar verwijzen; de
 * effectieve start-clamp gebeurt nu server-side.)
 */
export const TRACKING_START_DATE = '2026-04-26';

/**
 * Load funnel counts for a given period.
 * @param {Date} startDate
 * @param {Date} endDate
 * @param {string} niche
 * @returns {{sessions:number, botSessions:number, matchedCalls:number, totalCalls:number}}
 */
export const loadFunnelData = async (startDate, endDate, niche = 'all') => {
  try {
    const res = await apiGet('/data/funnel', {
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      niche,
    });
    return {
      sessions: res.sessions || 0,
      botSessions: res.botSessions || 0,
      matchedCalls: res.matchedCalls || 0,
      totalCalls: res.totalCalls || 0,
    };
  } catch (error) {
    console.error('Error loading funnel data:', error);
    return { sessions: 0, botSessions: 0, matchedCalls: 0, totalCalls: 0 };
  }
};

/**
 * Load top pages by distinct visitor count.
 * @returns {Array<{url:string, count:number}>}
 */
export const loadTopPages = async (startDate, endDate, niche = 'all', limit = 10) => {
  try {
    return await apiGet('/data/top-pages', {
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      niche,
      limit,
    });
  } catch (error) {
    console.error('Error loading top pages:', error);
    return [];
  }
};

/**
 * Load match-method breakdown for qooqie_leads in period.
 * Naast de buckets ook avgConfidence (gewogen gemiddelde match_confidence).
 */
export const loadMatchMethodBreakdown = async (startDate, endDate, niche = 'all') => {
  try {
    return await apiGet('/data/match-breakdown', {
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      niche,
    });
  } catch (error) {
    console.error('Error loading match method breakdown:', error);
    return {
      timestamp_phone: 0,
      timestamp: 0,
      session_daily_unique: 0,
      session_only: 0,
      session_pool: 0,
      session_daily_multiple: 0,
      timestamp_recurring: 0,
      unmatched: 0,
      avgConfidence: null,
    };
  }
};

/**
 * Attributie-bundel: bronnen (sessies + calls + conversie), landingspagina's
 * (idem) en bezoekers-totalen. Vervangt de losse top-landing-pages en
 * source-attribution calls. sessions/conversion/visitors zijn null zolang de
 * RPC funnel_session_aggregates niet beschikbaar is (calls blijven werken).
 * @returns {{sources:Array, landingPages:Array, visitors:{total:number,returning:number}|null}}
 */
export const loadAttribution = async (startDate, endDate, niche = 'all', limit = 10) => {
  try {
    const res = await apiGet('/data/attribution', {
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      niche,
      limit,
    });
    return {
      sources: res.sources || [],
      landingPages: res.landingPages || [],
      topPages: res.topPages || [],
      visitors: res.visitors || null,
    };
  } catch (error) {
    console.error('Error loading attribution:', error);
    return { sources: [], landingPages: [], topPages: [], visitors: null };
  }
};

/**
 * Derive a short display label from a full page URL.
 * Strips protocol + www, keeps path, truncates long paths.
 */
export const formatPageUrl = (url, maxLength = 55) => {
  try {
    const parsed = new URL(url);
    const path = parsed.hostname.replace(/^www\./, '') + parsed.pathname;
    return path.length > maxLength ? path.slice(0, maxLength - 1) + '…' : path;
  } catch {
    return url.length > maxLength ? url.slice(0, maxLength - 1) + '…' : url;
  }
};
